// ================================================================
// 命盤查詢頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-23
// 功能：顯示使用者的紫微斗數命盤資訊和圖表
// ================================================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { queryChart, ChartResponse, formatBirthTime } from '../api/chart';

/**
 * 將時辰代碼轉換為時辰名稱
 */
/**
 * 命盤查詢頁面組件
 */
const ChartPage: React.FC = () => {
  const { user } = useAuth();
  const [chartData, setChartData] = useState<ChartResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // 載入命盤資料
  useEffect(() => {
    const loadChart = async () => {
      if (!user?.birth_time || !user?.gender) {
        setError('無法取得使用者出生資料');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const result = await queryChart(user.birth_time, user.gender);
        setChartData(result);
      } catch (err: any) {
        setError(err.message || '命盤查詢失敗');
      } finally {
        setLoading(false);
      }
    };

    loadChart();
  }, [user]);

  // 載入中
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // 錯誤
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // 無資料
  if (!chartData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">尚未取得命盤資料</Alert>
      </Container>
    );
  }

  const { data: rawData } = chartData;

  // 轉換陣列格式為物件格式（後端返回陣列，需要轉換）
  const data = Array.isArray(rawData) ? {
    InputTimeLabel: rawData[0],
    Gender: rawData[1],
    ImageYearsOld: rawData[2],
    BornYear_Zi: rawData[3],
    YinYangNumber: rawData[4],
    Result_StarAllInformation: rawData[5],
    TwelveGrids: rawData[6],
    FourResolveGonGanInfoMatrix: rawData[7],
    // 從 TwelveGrids 中找到命宮位置
    MingGonPosition: (() => {
      const mingGongItem = rawData[6]?.find((item: any) => item.Purple_SquareReOrder_MinGon === '命宮');
      return mingGongItem?.TwelveGridsNumber || rawData[7]?.[0]?.[0];
    })(),
    // 從星曜資訊中找到身宮位置
    ShenGonPosition: (() => {
      const shenGongStar = rawData[5]?.find((star: any) => star.StarsChineseName === '身宮');
      return shenGongStar?.Result_StarPosition || rawData[7]?.[1]?.[0];
    })(),
    Result_StarPosition: rawData[9],
    GridWeightValue_3Way4Front: rawData[10],
    Big5ValueOf12Gons: rawData[11],
    BigLimitSequence: rawData[12],
    GridValue_12BigLimit: rawData[13],
    GridValue_12SmallLimit_Final: rawData[14],
    GridValue_12FlowMonth_Final: rawData[15],
    WorkFit_Distance: rawData[16],
    LunaryConvertResult: rawData[17],
    OtherInformationList: rawData[18]
  } : rawData;

  // 地支名稱（對應位置 1-12）
  const dizhiNames = ['寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥', '子', '丑'];

  // 功能宮位名稱（按順序：命宮、父母宮、福德宮、田宅宮、事業宮、交友宮、遷移宮、疾厄宮、財帛宮、子女宮、夫妻宮、兄弟宮）
  const functionalPalaceNames = [
    '命宮', '父母宮', '福德宮', '田宅宮', '事業宮', '交友宮',
    '遷移宮', '疾厄宮', '財帛宮', '子女宮', '夫妻宮', '兄弟宮'
  ];

  // 取得功能宮位名稱（根據順序索引 0-11）
  const getFunctionalPalaceName = (orderIndex: number): string => {
    if (orderIndex >= 0 && orderIndex < 12) {
      return functionalPalaceNames[orderIndex];
    }
    return 'N/A';
  };

  // 取得某個宮位的星曜
  const getStarsInGong = (gongPosition: number) => {
    const starsInfo = data.Result_StarAllInformation;
    const stars = {
      level1: [] as string[],  // 主星
      level2: [] as string[],  // 輔星
      level3: [] as string[],  // 雜曜
      level4: [] as string[],  // 四化星
      level10: [] as string[]  // 宮位標記（大限/小限/流年/流月）
    };

    // 檢查是否有星曜資料
    if (!starsInfo || !Array.isArray(starsInfo)) {
      return stars;
    }

    // 要顯示的 level 10 宮位標記
    const showLevel10Names = ['大限宮位', '小限宮位', '流年宮位', '流月宮位'];

    // 後端返回的是物件陣列格式
    for (let i = 0; i < starsInfo.length; i++) {
      const star = starsInfo[i];
      if (star.Result_StarPosition === gongPosition) {
        const level = star.StarsLevel;
        const name = star.StarsChineseName;

        if (level === 1) stars.level1.push(name);
        else if (level === 2) stars.level2.push(name);
        else if (level === 3) stars.level3.push(name);
        else if (level === 4) stars.level4.push(name);
        else if (level === 10 && showLevel10Names.includes(name)) {
          stars.level10.push(name);
        }
      }
    }

    return stars;
  };

  // 計算12宮位在12大限中的平均運勢
  const calculateBigLimitAverages = () => {
    if (!data.GridValue_12BigLimit) {
      return null;
    }

    const bigLimitMatrix = data.GridValue_12BigLimit;
    const averages: number[] = [];

    for (let gongOrder = 0; gongOrder < 12; gongOrder++) {
      let sum = 0;
      for (let limitIndex = 0; limitIndex < 12; limitIndex++) {
        sum += bigLimitMatrix[limitIndex][gongOrder];
      }
      averages.push(sum / 12);
    }

    return averages;
  };

  const bigLimitAverages = calculateBigLimitAverages();
  const overallAverage = bigLimitAverages ? bigLimitAverages.reduce((a, b) => a + b, 0) / 12 : null;
  const maxAverage = bigLimitAverages ? Math.max(...bigLimitAverages) : null;
  const minAverage = bigLimitAverages ? Math.min(...bigLimitAverages) : null;
  const bestGongIndex = bigLimitAverages && maxAverage !== null ? bigLimitAverages.indexOf(maxAverage) : -1;
  const worstGongIndex = bigLimitAverages && minAverage !== null ? bigLimitAverages.indexOf(minAverage) : -1;

  // 計算目前所在大限
  const currentBigLimit = (() => {
    if (!data.TwelveGrids) return null;
    const virtualAge = Number(data.ImageYearsOld);
    const birthYear = parseInt(chartData.birth_time?.substring(0, 4) || String(data.InputTimeLabel).substring(0, 4));
    if (isNaN(virtualAge) || virtualAge <= 0 || isNaN(birthYear)) return null;

    for (let i = 0; i < data.TwelveGrids.length; i++) {
      const grid = data.TwelveGrids[i];
      const raw = String(grid.BigLimitYear_MingonReOrderList || '');
      const match = raw.match(/(\d+)歲至(\d+)歲/);
      if (!match) continue;
      const startAge = parseInt(match[1]);
      const endAge = parseInt(match[2]);
      if (virtualAge >= startAge && virtualAge <= endAge) {
        return {
          palaceName: String(grid.Purple_SquareReOrder_MinGon),
          startAge,
          endAge,
          startYear: birthYear + startAge - 1,
          endYear: birthYear + endAge - 1,
        };
      }
    }
    return null;
  })();

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: '#667eea',
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
        }}
      >
        紫微斗數命盤
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        gutterBottom
        sx={{
          mb: 3,
          fontSize: { xs: '0.9rem', sm: '1rem' }
        }}
      >
        查看您的紫微斗數命盤和基本資料
      </Typography>

      {/* 基本資料 */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: '#764ba2',
            fontSize: { xs: '1.15rem', sm: '1.3rem' }
          }}
        >
          基本資料
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              出生時間
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
              {formatBirthTime(data.InputTimeLabel)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              性別
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
              {data.Gender === 1 ? '男' : '女'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              虛歲
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
              {data.ImageYearsOld} 歲
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              陰陽局數
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
              {data.YinYangNumber} 局
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              命宮位置
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
              {data.MingGonPosition >= 1 && data.MingGonPosition <= 12 ? dizhiNames[data.MingGonPosition - 1] : ''}宮
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              身宮位置
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
              {data.ShenGonPosition >= 1 && data.ShenGonPosition <= 12 ? dizhiNames[data.ShenGonPosition - 1] : ''}宮
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              大限順序
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
              {data.BigLimitSequence === 'forward' ? '順行' : '逆行'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>


      {/* 宮位資訊 */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: '#764ba2',
            fontSize: { xs: '1.15rem', sm: '1.3rem' }
          }}
        >
          宮位資訊
        </Typography>

        {/* 星曜圖例 */}
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1.5, sm: 2 },
          mb: 2,
          p: { xs: 1, sm: 1.5 },
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          alignItems: 'center'
        }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mr: 1, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
          >
            星曜分類：
          </Typography>
          <Chip
            label="主星"
            size="small"
            sx={{
              backgroundColor: '#1B1B1B',
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.75rem', sm: '0.85rem' }
            }}
          />
          <Chip
            label="輔星"
            size="small"
            sx={{
              backgroundColor: '#4A148C',
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.75rem', sm: '0.85rem' }
            }}
          />
          <Chip
            label="雜曜"
            size="small"
            sx={{
              backgroundColor: '#B71C1C',
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.75rem', sm: '0.85rem' }
            }}
          />
          <Chip
            label="四化星"
            size="small"
            sx={{
              backgroundColor: '#1565C0',
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.75rem', sm: '0.85rem' }
            }}
          />
          <Chip
            label="宮位"
            size="small"
            sx={{
              backgroundColor: '#2E7D32',
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.75rem', sm: '0.85rem' }
            }}
          />
        </Box>

        <Grid container spacing={2}>
          {Array.from({ length: 12 }, (_, index) => {
            const gongPosition = index + 1; // 地支位置 1-12
            // 從 TwelveGrids 中找到該地支位置對應的功能宮位名稱
            const gongItem = data.TwelveGrids?.find((item: any) => item.TwelveGridsNumber === gongPosition);
            const gongName = gongItem?.Purple_SquareReOrder_MinGon || `第${gongPosition}宮`;

            const stars = getStarsInGong(gongPosition);
            const isMinggong = gongPosition === data.MingGonPosition;
            const isShengong = gongPosition === data.ShenGonPosition;

            return (
              <Grid item xs={12} sm={6} md={4} key={gongPosition}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: { xs: '1.1rem', sm: '1.25rem' },
                          color: '#C62828'
                        }}
                      >
                        {gongName}
                      </Typography>
                      {isMinggong && (
                        <Chip label="命宮" size="small" color="primary" sx={{ ml: 1 }} />
                      )}
                      {isShengong && (
                        <Chip label="身宮" size="small" color="secondary" sx={{ ml: 1 }} />
                      )}
                    </Box>

                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 1.5, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                    >
                      {dizhiNames[gongPosition - 1]}宮 (第 {gongPosition} 宮)
                    </Typography>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Level 1 主星 - 最大字體，黑色 */}
                    {stars.level1.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography
                          sx={{
                            color: '#1B1B1B',
                            fontWeight: 'bold',
                            fontSize: { xs: '1.1rem', sm: '1.2rem' },
                            letterSpacing: '1px',
                            lineHeight: 1.6
                          }}
                        >
                          {stars.level1.join(' ')}
                        </Typography>
                      </Box>
                    )}

                    {/* Level 2 輔星 - 中等字體，深紫色 */}
                    {stars.level2.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          sx={{
                            color: '#4A148C',
                            fontWeight: 600,
                            fontSize: { xs: '1rem', sm: '1.05rem' },
                            lineHeight: 1.5
                          }}
                        >
                          {stars.level2.join(' ')}
                        </Typography>
                      </Box>
                    )}

                    {/* Level 3 雜曜 - 中小字體，深紅色 */}
                    {stars.level3.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          sx={{
                            color: '#B71C1C',
                            fontWeight: 500,
                            fontSize: { xs: '0.9rem', sm: '0.95rem' },
                            lineHeight: 1.5
                          }}
                        >
                          {stars.level3.join(' ')}
                        </Typography>
                      </Box>
                    )}

                    {/* Level 4 四化星 - 中小字體，深藍色 */}
                    {stars.level4.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          sx={{
                            color: '#1565C0',
                            fontWeight: 500,
                            fontSize: { xs: '0.9rem', sm: '0.95rem' },
                            lineHeight: 1.5
                          }}
                        >
                          {stars.level4.join(' ')}
                        </Typography>
                      </Box>
                    )}

                    {/* Level 10 宮位標記 - 綠色 */}
                    {stars.level10.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {stars.level10.map((name, idx) => (
                          <Chip
                            key={idx}
                            label={name.replace('宮位', '命宮')}
                            size="small"
                            sx={{
                              backgroundColor: '#2E7D32',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              height: '22px'
                            }}
                          />
                        ))}
                      </Box>
                    )}

                    {/* 無星曜提示 */}
                    {stars.level1.length === 0 && stars.level2.length === 0 &&
                     stars.level3.length === 0 && stars.level4.length === 0 && (
                      <Typography
                        color="text.secondary"
                        sx={{ fontStyle: 'italic', fontSize: { xs: '0.9rem', sm: '1rem' } }}
                      >
                        無星曜
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* 命盤格局圖 */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: 'bold', color: '#764ba2', fontSize: { xs: '1.15rem', sm: '1.3rem' } }}
        >
          命盤格局圖
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
          可左右滑動查看完整命盤
        </Typography>

        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {(() => {
            // 4×4 格局：宮位 → 格位對應
            // 標準紫微斗數命盤：寅(1)左下角，順時針排列（左欄由下往上）
            //   頂排：巳(4)  午(5)  未(6)  申(7)
            //   左欄：辰(3) [中央] [中央] 酉(8)
            //         卯(2) [中央] [中央] 戌(9)
            //   底排：寅(1)  丑(12) 子(11) 亥(10)
            const gridPlacement: Record<number, { row: number; col: number }> = {
              4:  { row: 1, col: 1 }, 5:  { row: 1, col: 2 },
              6:  { row: 1, col: 3 }, 7:  { row: 1, col: 4 },
              3:  { row: 2, col: 1 }, 8:  { row: 2, col: 4 },
              2:  { row: 3, col: 1 }, 9:  { row: 3, col: 4 },
              1:  { row: 4, col: 1 }, 12: { row: 4, col: 2 },
              11: { row: 4, col: 3 }, 10: { row: 4, col: 4 },
            };

            // 五行局名稱
            const wuxingMap: Record<number, string> = {
              2: '水二局', 3: '木三局', 4: '金四局', 5: '土五局', 6: '火六局'
            };
            const wuxingJu = (() => {
              const oi = data.OtherInformationList;
              if (Array.isArray(oi) && oi.length > 3 && oi[3]) return String(oi[3]);
              const n = Number(data.YinYangNumber);
              return wuxingMap[n] || `${n}局`;
            })();

            const palacePositions: number[] = [4, 5, 6, 7, 3, 8, 2, 9, 1, 12, 11, 10];

            return (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))',
                gridTemplateRows: 'repeat(4, auto)',
                minWidth: '600px',
                gap: '1px',
                backgroundColor: '#aaa',
                border: '2px solid #764ba2',
                borderRadius: '4px',
                overflow: 'hidden',
              }}>
                {/* 12 個宮位 */}
                {palacePositions.map(pos => {
                  const pl = gridPlacement[pos];
                  const stars = getStarsInGong(pos);
                  const gongItem = data.TwelveGrids?.find((item: any) => item.TwelveGridsNumber === pos);
                  const funcName = gongItem?.Purple_SquareReOrder_MinGon ? String(gongItem.Purple_SquareReOrder_MinGon) : '';
                  // Purple_Monthganzi_list 已含完整干支（如"壬寅"），直接使用
                  const ganZhi = gongItem?.Purple_Monthganzi_list ? String(gongItem.Purple_Monthganzi_list) : '';
                  const bigLimitRaw = gongItem?.BigLimitYear_MingonReOrderList;
                  const bigLimitAge = (bigLimitRaw !== undefined && bigLimitRaw !== null) ? Number(bigLimitRaw) : null;
                  const isMinggong = pos === data.MingGonPosition;
                  const isShenGong = pos === data.ShenGonPosition;
                  const bgColor = isMinggong ? '#FCE4EC' : (isShenGong ? '#E8EAF6' : 'white');

                  return (
                    <Box
                      key={pos}
                      sx={{
                        gridRow: pl.row,
                        gridColumn: pl.col,
                        p: { xs: 0.75, sm: 1 },
                        backgroundColor: bgColor,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.3,
                        minHeight: { xs: '140px', sm: '160px' },
                      }}
                    >
                      {/* 干支 + 大限年齡 */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, color: '#555', fontWeight: 500 }}>
                          {ganZhi}
                        </Typography>
                        {bigLimitAge !== null && !isNaN(bigLimitAge) && (
                          <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: '#888' }}>
                            {bigLimitAge}–{bigLimitAge + 9}歲
                          </Typography>
                        )}
                      </Box>

                      {/* 功能宮名 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: { xs: '0.88rem', sm: '1rem' }, fontWeight: 'bold', color: '#C62828', lineHeight: 1.3 }}>
                          {funcName}
                        </Typography>
                        {isMinggong && (
                          <Typography component="span" sx={{ fontSize: { xs: '0.7rem', sm: '0.78rem' }, color: '#1565C0', fontWeight: 'bold' }}>命</Typography>
                        )}
                        {isShenGong && (
                          <Typography component="span" sx={{ fontSize: { xs: '0.7rem', sm: '0.78rem' }, color: '#6A1B9A', fontWeight: 'bold' }}>身</Typography>
                        )}
                      </Box>

                      <Box sx={{ borderBottom: '1px solid #ddd' }} />

                      {/* 主星 level1 — 黑色粗體 */}
                      {stars.level1.length > 0 && (
                        <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1.08rem' }, fontWeight: 'bold', color: '#1B1B1B', lineHeight: 1.4 }}>
                          {stars.level1.join(' ')}
                        </Typography>
                      )}
                      {/* 輔星 level2 — 深紫色 */}
                      {stars.level2.length > 0 && (
                        <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: '#4A148C', lineHeight: 1.3 }}>
                          {stars.level2.join(' ')}
                        </Typography>
                      )}
                      {/* 四化 level4 — 深藍色 */}
                      {stars.level4.length > 0 && (
                        <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.88rem' }, color: '#1565C0', lineHeight: 1.3 }}>
                          {stars.level4.join(' ')}
                        </Typography>
                      )}
                      {/* 雜曜 level3 — 深紅色 */}
                      {stars.level3.length > 0 && (
                        <Typography sx={{ fontSize: { xs: '0.74rem', sm: '0.84rem' }, color: '#B71C1C', lineHeight: 1.3 }}>
                          {stars.level3.join(' ')}
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                {/* 中央格（合併 row2-3, col2-3） */}
                <Box sx={{
                  gridRow: '2 / 4',
                  gridColumn: '2 / 4',
                  p: 1.5,
                  backgroundColor: '#FAFAFA',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  minHeight: '240px',
                }}>
                  {/* 第一行：系統名稱 */}
                  <Typography sx={{
                    fontSize: { xs: '0.78rem', sm: '0.88rem' },
                    fontWeight: 'bold',
                    color: '#764ba2',
                    textAlign: 'center',
                    lineHeight: 1.4,
                  }}>
                    我的好運氣{'\n'}紫微斗數分析系統
                  </Typography>
                  <Box sx={{ borderBottom: '1px solid #ddd', width: '80%' }} />
                  {/* 第二行：出生時間 */}
                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: '#333', textAlign: 'center' }}>
                    {formatBirthTime(data.InputTimeLabel)}
                  </Typography>
                  {/* 性別 */}
                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: '#555', textAlign: 'center' }}>
                    {data.Gender === 1 ? '男命' : '女命'}
                  </Typography>
                  <Box sx={{ borderBottom: '1px solid #eee', width: '60%' }} />
                  {/* 命宮 / 身宮 */}
                  <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.92rem' }, color: '#444', textAlign: 'center' }}>
                    命宮：{data.MingGonPosition >= 1 && data.MingGonPosition <= 12
                      ? `${dizhiNames[data.MingGonPosition - 1]}宮` : '—'}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.92rem' }, color: '#444', textAlign: 'center' }}>
                    身宮：{data.ShenGonPosition >= 1 && data.ShenGonPosition <= 12
                      ? `${dizhiNames[data.ShenGonPosition - 1]}宮` : '—'}
                  </Typography>
                  {/* 納音五行 */}
                  {wuxingJu && (
                    <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, color: '#666', textAlign: 'center' }}>
                      {wuxingJu}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })()}
        </Box>

        {/* 圖例 */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">圖例：</Typography>
          {[
            { label: '主星', color: '#1B1B1B' },
            { label: '輔星', color: '#4A148C' },
            { label: '四化', color: '#1565C0' },
            { label: '雜曜', color: '#B71C1C' },
          ].map(({ label, color }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color }} />
              <Typography variant="caption" sx={{ color, fontSize: '0.72rem' }}>{label}</Typography>
            </Box>
          ))}
          <Typography variant="caption" sx={{ color: '#FCE4EC', backgroundColor: '#9E9E9E', px: 0.5, borderRadius: 0.5, fontSize: '0.72rem' }}>命宮底色</Typography>
          <Typography variant="caption" sx={{ color: '#E8EAF6', backgroundColor: '#9E9E9E', px: 0.5, borderRadius: 0.5, fontSize: '0.72rem' }}>身宮底色</Typography>
        </Box>
      </Paper>

      {/* 運勢分析 */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: '#764ba2',
            fontSize: { xs: '1.15rem', sm: '1.3rem' }
          }}
        >
          大限運勢分析
        </Typography>
        <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
          如需更詳細的運勢分析，請前往「問題查詢」選擇您感興趣的問題
        </Alert>
        {currentBigLimit && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f3e5f5', borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#764ba2', fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
              目前大限：{currentBigLimit.palaceName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#555', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
              虛歲 {currentBigLimit.startAge}–{currentBigLimit.endAge} 歲（西元 {currentBigLimit.startYear}–{currentBigLimit.endYear} 年）
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  大限運勢平均
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
                  {overallAverage !== null ? overallAverage.toFixed(2) : 'N/A'} 分
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  運勢最佳宮位
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
                  {bestGongIndex >= 0 ? getFunctionalPalaceName(bestGongIndex) : 'N/A'}
                </Typography>
                {maxAverage !== null && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    ({maxAverage.toFixed(2)} 分)
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  運勢最弱宮位
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="error.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
                  {worstGongIndex >= 0 ? getFunctionalPalaceName(worstGongIndex) : 'N/A'}
                </Typography>
                {minAverage !== null && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    ({minAverage.toFixed(2)} 分)
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* 更新資訊 */}
      <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
          FlowTime 已更新: {chartData.flowtime_updated ? '是' : '否'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
          查詢時間: {new Date(chartData.timestamp).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })}
        </Typography>
      </Paper>
    </Container>
  );
};

export default ChartPage;
